import React, { useState, useContext, useEffect, useRef } from "react";
import "./PanelFilter.scss";
import { color, flex, width } from "@mui/system";
import { Radios } from "../../../../components/RadioGroup/Radios";
import BasicDatePicker from "../../../../components/Datepicker/Datepicker";
import { Filter } from "@mui/icons-material";
import { FilterDateTime } from "./FiltersPanels/FilterDateTime";
import { FilterDropdown } from "./FiltersPanels/FilterDropdown";
import {
  getControlCenter,
  getFilterByControlGroup,
  getConsumeDataByPanelIdWithFilters,
  saveControlFilters,
} from "../../services/DashboardConfigurationService";
import { useGridStackContext } from "../../../../contexts/grid-stack-context";

const PanelFilter = ({ open, onClose, dashboardId, tabId }) => {
  // Estados para los datos de los servicios
  const [controlCenter, setControlCenter] = useState(null);
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtersActionsUser, setFiltersActionsUser] = useState([]);
  const [filterValuesActions, setFilterValuesActions] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [clearAllTrigger, setClearAllTrigger] = useState(0);
  const [panelLoadingStates, setPanelLoadingStates] = useState({}); // Estado de loading por panel

  const { updatePanelData, panels } = useGridStackContext();

  // Ref para controlar el estado anterior y evitar dobles llamadas
  const prevStateRef = useRef({
    dashboardId: null,
    tabId: null,
    panelCount: 0,
  });
  const isLoadingRef = useRef(false);

  // Funciones para manejar el estado de loading de paneles
  const setPanelLoading = (panelId, isLoading) => {
    setPanelLoadingStates((prev) => ({
      ...prev,
      [panelId]: isLoading,
    }));

    // Actualizar el panel con estado de loading
    updatePanelData(parseInt(panelId), {
      isLoadingFilters: isLoading,
    });
  };

  const clearAllPanelLoadings = () => {
    Object.keys(panelLoadingStates).forEach((panelId) => {
      updatePanelData(parseInt(panelId), {
        isLoadingFilters: false,
      });
    });
    setPanelLoadingStates({});
  };

  // Datos de ejemplo (se pueden mantener como fallback)
  const filtersTest = [
    {
      id: 1,
      title: "Date and Time",
      type: "date",
      isRangeBased: "manual",
      initialDate: null,
      finalDate: null,
    },
    {
      id: 2,
      title: "Airports",
      type: "search",
      options: [
        { value: "MIA", label: "MIA" },
        { value: "JFK", label: "JFK" },
      ],
    },
  ];

  // Función reutilizable para cargar los datos de filtros
  const fetchFiltersData = async () => {
    if (!dashboardId || !tabId) {
      console.warn("dashboardId or tabId not provided");
      return;
    }

    // Prevenir llamadas duplicadas
    if (isLoadingRef.current) {
      console.log("� fetchFiltersData already in progress, skipping...");
      return;
    }

    isLoadingRef.current = true;
    console.log("�🔄 Loading filter data...");
    setLoading(true);
    setError(null);

    try {
      const accountId = localStorage.getItem("u_account_id");

      // 1. Obtener el control center
      const controlCenterData = await getControlCenter(
        accountId,
        dashboardId,
        tabId
      );
      setControlCenter(controlCenterData);

      // 2. Si hay control center y filterGroups, obtener los filtros de cada grupo
      if (
        controlCenterData &&
        controlCenterData.filterGroups &&
        controlCenterData.filterGroups.length > 0
      ) {
        const allFilters = [];

        // Recorrer cada grupo de filtros
        for (const filterGroup of controlCenterData.filterGroups) {
          try {
            console.log(
              `Obteniendo filtros para el grupo: ${filterGroup.filterGroupName} (ID: ${filterGroup.id})`
            );

            // Llamar al servicio para obtener los filtros de este grupo específico
            const groupFiltersData = await getFilterByControlGroup(
              filterGroup.id
            );

            // Agregar información del grupo a cada filtro
            const filtersWithGroupInfo = {
              filterGroup,
              filters: groupFiltersData,
            };

            // Agregar este grupo al array principal (push del objeto, no spread)
            allFilters.push(filtersWithGroupInfo);

            // console.log(
            //   `Filtros obtenidos del grupo ${filterGroup.filterGroupName}:`,
            //   filtersWithGroupInfo
            // );
          } catch (groupError) {
            console.error(
              `Error obteniendo filtros del grupo ${filterGroup.filterGroupName}:`,
              groupError
            );
            // Continuar con el siguiente grupo aunque uno falle
          }
        }

        console.log("✅ Todos los filtros obtenidos:", allFilters);
        setFilters(allFilters);
      } else {
        console.warn("No hay filterGroups en el control center");
        setFilters([]);
      }
    } catch (err) {
      console.error("Error fetching filters data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  };

  // 🎯 useEffect inteligente para manejar cambios de tab y paneles sin doble consumo
  useEffect(() => {
    const currentPanelCount = Object.keys(panels).length;
    const prevState = prevStateRef.current;

    // Solo proceder si tenemos dashboardId y tabId
    if (!dashboardId || !tabId) {
      console.warn("dashboardId or tabId not provided");
      return;
    }

    // Detectar qué tipo de cambio ocurrió
    const tabChanged =
      prevState.dashboardId !== dashboardId || prevState.tabId !== tabId;
    const panelCountChanged = prevState.panelCount !== currentPanelCount;

    // Log para debugging
    if (tabChanged) {
      console.log(`🔄 Tab changed: ${prevState.tabId} -> ${tabId}`);
    }
    if (panelCountChanged) {
      console.log(
        `📊 Panel count changed: ${prevState.panelCount} -> ${currentPanelCount}`
      );
    }

    // Actualizar el estado anterior
    prevStateRef.current = {
      dashboardId,
      tabId,
      panelCount: currentPanelCount,
    };

    if (currentPanelCount > 0) {
      // Hay paneles: cargar filtros
      console.log(
        `🔄 Loading filters for tab ${tabId} with ${currentPanelCount} panels...`
      );

      // Delay más corto para cambio de tab, más largo para cambio de paneles
      const delay = tabChanged && !panelCountChanged ? 100 : 300;

      const timeoutId = setTimeout(() => {
        fetchFiltersData();
      }, delay);

      return () => clearTimeout(timeoutId);
    } else {
      // No hay paneles: limpiar filtros inmediatamente
      console.log("📭 No panels found, clearing filters...");
      setFilters([]);
      setControlCenter(null);
      setFiltersActionsUser([]);
      setError(null);
      setLoading(false);
    }
  }, [dashboardId, tabId, Object.keys(panels).length]); // Detectar cambios en tab Y cambios en cantidad de paneles

  // Función para restaurar paneles afectados por un filtro específico
  const restorePanelsAffectedByFilter = async (filterId) => {
    try {
      console.log(
        `🔄 Restaurando paneles afectados por el filtro ${filterId}...`
      );

      if (!controlCenter || !controlCenter.filterPanelMappings) {
        console.warn("No hay filterPanelMappings disponibles");
        return;
      }

      // Encontrar todos los paneles que usan este filtro específico
      const affectedPanels = controlCenter.filterPanelMappings
        .filter((mapping) => mapping.panelFilterId.filterId === filterId)
        .map((mapping) => mapping.panelId);

      // Remover duplicados
      const uniqueAffectedPanels = [...new Set(affectedPanels)];

      console.log(
        `📋 Paneles afectados por filtro ${filterId}:`,
        uniqueAffectedPanels
      );

      // Mostrar loading en los paneles que se van a restaurar
      uniqueAffectedPanels.forEach((panelId) => {
        setPanelLoading(panelId, true);
      });

      // Restaurar cada panel afectado con datos sin filtros
      for (const panelId of uniqueAffectedPanels) {
        try {
          console.log(`🔄 Restaurando panel ${panelId} a datos originales...`);

          // Consumir el servicio sin filtros para obtener datos originales
          const originalPanelData = await getConsumeDataByPanelIdWithFilters(
            parseInt(panelId),
            [] // Sin filtros = datos originales
          );

          console.log(
            `📊 Datos originales del panel ${panelId}:`,
            originalPanelData
          );

          // Transformar los datos al formato esperado
          const newData = originalPanelData.data
            ? originalPanelData.data.map((row) => {
                const obj = {};
                originalPanelData.columns.forEach((column, index) => {
                  obj[column] = row[index];
                });
                return obj;
              })
            : [];

          // Actualizar el panel con los datos originales
          updatePanelData(parseInt(panelId), {
            data: newData,
            isLoadingFilters: false, // Quitar loading cuando se complete
          });

          // Actualizar estado local de loading
          setPanelLoadingStates((prev) => ({
            ...prev,
            [panelId]: false,
          }));

          console.log(
            `✅ Panel ${panelId} restaurado exitosamente a datos originales`
          );
        } catch (error) {
          console.error(`❌ Error restaurando panel ${panelId}:`, error);

          // Quitar loading en caso de error
          setPanelLoading(panelId, false);
        }
      }
    } catch (error) {
      console.error("❌ Error en restorePanelsAffectedByFilter:", error);
    }
  };

  // Función para aplicar solo un filtro específico (cuando se presiona "Apply Selection")
  const applyIndividualFilter = async (filterValues) => {
    console.log("🎯 APLICANDO FILTRO INDIVIDUAL:", filterValues);

    try {
      const filterPanelMappings = controlCenter.filterPanelMappings;
      const filtersToApply = [];

      // Procesar solo este filtro específico
      const { filterID, filterCompositions } = filterValues;

      filterCompositions.forEach((composition) => {
        const { id: compositionId, value } = composition;

        // Buscar en filterPanelMappings los paneles que usan este filtro y composición
        const matchingConfigs = filterPanelMappings.filter(
          (mapping) =>
            mapping.panelFilterId.filterId === filterID &&
            mapping.panelFilterId.filterCompositionId === compositionId
        );

        // Para cada configuración que coincida
        matchingConfigs.forEach((config) => {
          const { panelId, panelFilterId } = config;
          const { parameterName, panelFiltersDatetimeConfigs } = panelFilterId;

          // Formatear el valor según la configuración
          let formattedValue = value;
          if (
            panelFiltersDatetimeConfigs &&
            panelFiltersDatetimeConfigs.format
          ) {
            formattedValue = formatValueAccordingToConfig(
              value,
              panelFiltersDatetimeConfigs.format
            );
          }

          // Agregar al array de filtros a aplicar
          filtersToApply.push({
            panelId: panelId,
            parameterName: parameterName,
            value: formattedValue,
            format: panelFiltersDatetimeConfigs?.format,
            filterId: filterID,
            filterCompositionId: compositionId,
          });
        });
      });

      console.log("🚀 Filtros individuales a aplicar:", filtersToApply);

      // Aplicar solo estos filtros específicos
      await applyFiltersToEachPanel(filtersToApply);

      console.log("✅ Filtro individual aplicado exitosamente");
    } catch (error) {
      console.error("❌ Error aplicando filtro individual:", error);
    }
  };

  const returnValues = async (values) => {
    console.log("=== RETURN VALUES LLAMADO ===");
    console.log("Returned Values:", values);
    console.log("filtersActionsUser ANTES:", filtersActionsUser);

    // 🆕 NUEVO: Si es aplicación individual, aplicar solo este filtro
    if (values.applyIndividual === true) {
      console.log("🎯 DETECTADO APPLY INDIVIDUAL - aplicando solo este filtro");

      // Primero actualizar el estado de filtersActionsUser
      const indexFilterId = filtersActionsUser.findIndex(
        (filter) => filter.filterID === values.filterID
      );

      let updatedFilters;
      if (indexFilterId !== -1) {
        updatedFilters = [...filtersActionsUser];
        updatedFilters[indexFilterId] = values;
      } else {
        updatedFilters = [...filtersActionsUser, values];
      }

      setFiltersActionsUser(updatedFilters);

      // Luego aplicar solo este filtro
      await applyIndividualFilter(values);
      return;
    }

    // Si el filtro se limpió, removerlo del array Y restaurar paneles afectados
    if (values.cleared === true) {
      console.log(
        `🧹 LIMPIANDO FILTRO ${values.filterID} - iniciando proceso...`
      );

      const updatedFilters = filtersActionsUser.filter(
        (filter) => filter.filterID !== values.filterID
      );
      setFiltersActionsUser(updatedFilters);
      console.log(
        `✅ Filter ${values.filterID} removed from filtersActionsUser`
      );
      console.log("filtersActionsUser DESPUÉS:", updatedFilters);

      // NUEVO: Restaurar inmediatamente los paneles afectados por este filtro
      console.log(
        `🔄 Restaurando paneles afectados por filtro ${values.filterID}...`
      );
      await restorePanelsAffectedByFilter(values.filterID);
      console.log(`✅ Paneles restaurados para filtro ${values.filterID}`);
      return;
    }

    // Lógica normal para agregar/actualizar filtros (sin aplicar automáticamente)
    const indexFilterId = filtersActionsUser.findIndex(
      (filter) => filter.filterID === values.filterID
    );
    if (indexFilterId !== -1) {
      const updatedFilters = [...filtersActionsUser];
      updatedFilters[indexFilterId] = values;
      setFiltersActionsUser(updatedFilters);
      return;
    }
    setFiltersActionsUser([...filtersActionsUser, values]);
  };

  // Manejar cambio en el input de búsqueda
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Filtrar grupos de filtros basándose en la búsqueda
  const filteredFilters = filters.filter((groupData) => {
    if (!searchValue.trim()) {
      return true; // Si no hay búsqueda, mostrar todos
    }

    const searchLower = searchValue.toLowerCase();

    // Buscar en el nombre del grupo o label
    const groupName = String(
      groupData.filterGroup?.filterGroupName || ""
    ).toLowerCase();
    const groupLabel = String(groupData.filterGroup?.label || "").toLowerCase();

    const groupMatches =
      groupName.includes(searchLower) || groupLabel.includes(searchLower);

    // Buscar en los filtros individuales del grupo
    const filterMatches = groupData.filters.some((filterItem) => {
      const filterName = String(
        filterItem.filter?.filterName || ""
      ).toLowerCase();
      const filterLabel = String(filterItem.label || "").toLowerCase();

      return (
        filterName.includes(searchLower) || filterLabel.includes(searchLower)
      );
    });

    // Retornar true si coincide el grupo O alguno de sus filtros
    return groupMatches || filterMatches;
  });

  // Función para limpiar todos los filtros
  const handleClearAllFilters = async () => {
    try {
      console.log("🧹 Clearing all filters - restoring default panel data...");

      // Antes de limpiar, restaurar los datos originales de todos los paneles
      if (controlCenter && controlCenter.filterPanelMappings) {
        // Obtener todos los panelIds únicos de los mappings
        const uniquePanelIds = [
          ...new Set(
            controlCenter.filterPanelMappings.map((mapping) => mapping.panelId)
          ),
        ];

        console.log("📋 Panels to restore:", uniquePanelIds);

        // Mostrar loading en todos los paneles que se van a restaurar
        uniquePanelIds.forEach((panelId) => {
          setPanelLoading(panelId, true);
        });

        // Restaurar cada panel con datos sin filtros
        for (const panelId of uniquePanelIds) {
          try {
            console.log(`🔄 Restoring default data for panel ${panelId}...`);

            // Consumir el servicio SIN filtros (array vacío) para obtener datos por defecto
            const panelDataWithoutFilters =
              await getConsumeDataByPanelIdWithFilters(
                parseInt(panelId),
                [] // Array vacío = sin filtros = datos por defecto
              );

            console.log(
              `📊 Default data restored for panel ${panelId}:`,
              panelDataWithoutFilters
            );

            // Actualizar el panel con los datos por defecto
            updatePanelData(parseInt(panelId), {
              data: panelDataWithoutFilters.data
                ? panelDataWithoutFilters.data.map((row) => {
                    const obj = {};
                    panelDataWithoutFilters.columns.forEach((column, index) => {
                      obj[column] = row[index];
                    });
                    return obj;
                  })
                : [],
              isLoadingFilters: false, // Quitar loading cuando se complete
              // NO incluir metadata para preservar la configuración original
            });

            // Actualizar estado local de loading
            setPanelLoadingStates((prev) => ({
              ...prev,
              [panelId]: false,
            }));

            console.log(
              `✅ Panel ${panelId} restored to default data successfully`
            );
          } catch (error) {
            console.error(`❌ Error restoring panel ${panelId}:`, error);

            // Quitar loading en caso de error
            setPanelLoading(panelId, false);
          }
        }
      }

      // Después de restaurar los paneles, limpiar los filtros
      setFiltersActionsUser([]);
      setClearAllTrigger((prev) => prev + 1); // Incrementar trigger para notificar a los hijos
      console.log("✅ All filters cleared - panels restored to default data");
    } catch (error) {
      console.error("❌ Error clearing all filters:", error);

      // Limpiar todos los loadings en caso de error
      clearAllPanelLoadings();
    }
  };

  // Aplicar filtros
  const handleApplyFilters = async () => {
    const filterPanelMappings = controlCenter.filterPanelMappings;
    const filterValuesActions = [];

    // Construir el array filterValuesActions con la estructura base
    for (let index = 0; index < filterPanelMappings.length; index++) {
      const appliedFilters = {
        panelId: filterPanelMappings[index].panelId,
        filterId: filterPanelMappings[index].panelFilterId.filterId,
        filterCompositionId:
          filterPanelMappings[index].panelFilterId.filterCompositionId,
        parameterName: filterPanelMappings[index].panelFilterId.parameterName,
        panelFiltersDatetimeConfigs:
          filterPanelMappings[index].panelFilterId.panelFiltersDatetimeConfigs,
      };
      filterValuesActions.push(appliedFilters);
    }

    // Array final con los filtros a aplicar a cada panel
    const filtersToApply = [];

    // Procesar cada filtro que el usuario quiere aplicar
    filtersActionsUser.forEach((userFilter) => {
      const { filterID, filterCompositions } = userFilter;

      // Para cada composición del filtro del usuario
      filterCompositions.forEach((composition) => {
        const { id: compositionId, value } = composition;

        // Buscar en filterValuesActions los paneles que usan este filtro y composición
        const matchingConfigs = filterValuesActions.filter(
          (config) =>
            config.filterId === filterID &&
            config.filterCompositionId === compositionId
        );

        // Para cada configuración que coincida
        matchingConfigs.forEach((config) => {
          const { panelId, parameterName, panelFiltersDatetimeConfigs } =
            config;

          // Formatear el valor según la configuración
          let formattedValue = value;
          if (
            panelFiltersDatetimeConfigs &&
            panelFiltersDatetimeConfigs.format
          ) {
            // Aquí puedes aplicar el formato específico si es necesario
            // Por ejemplo, formatear fechas según el formato especificado
            formattedValue = formatValueAccordingToConfig(
              value,
              panelFiltersDatetimeConfigs.format
            );
          }

          // Agregar al array final de filtros a aplicar
          filtersToApply.push({
            panelId: panelId,
            parameterName: parameterName,
            value: formattedValue,
            format: panelFiltersDatetimeConfigs?.format,
            filterId: filterID,
            filterCompositionId: compositionId,
          });
        });
      });
    });

    console.log("Filtros a aplicar a los paneles:", filtersToApply);

    // Aplicar los filtros a cada panel consumiendo el servicio
    await applyFiltersToEachPanel(filtersToApply);

    // Guardar los filtros aplicados usando saveControlFilters
    // try {
    //   console.log("Guardando filtros aplicados...");
    //   console.log("filtersActionsUser:", filtersActionsUser);

    //   const accountId = parseInt(localStorage.getItem("u_account_id"));
    //   const userId = parseInt(localStorage.getItem("u_id")); // Corregido de u_user_id a u_id
    //   const centerFilterId = controlCenter.id;

    //   // Preparar los filtros en el formato requerido para saveControlFilters
    //   const filtersToSave = [];
    //   let ordering = 1;

    //   filtersActionsUser.forEach((userFilter) => {
    //     const { filterID, filterCompositions, filterType, selectionTimeMode } =
    //       userFilter;

    //     console.log("Procesando userFilter:", userFilter);

    //     if (filterCompositions && filterCompositions.length > 0) {
    //       filterCompositions.forEach((composition) => {
    //         const { id: compositionId, value, displayValue } = composition;

    //         console.log("Procesando composition:", composition);

    //         // Validar que los valores requeridos no sean null/undefined
    //         if (compositionId != null && value != null) {
    //           filtersToSave.push({
    //             ordering: ordering++,
    //             selectionTimeMode: selectionTimeMode || "string", // Usar selectionTimeMode del filtro
    //             value: String(value), // Convertir a string
    //             displayValue: displayValue || String(value), // Usar displayValue si existe, sino el value
    //             filterCompositionId: parseInt(compositionId),
    //           });
    //         } else {
    //           console.warn(
    //             "Skipping composition with null/undefined values:",
    //             composition
    //           );
    //         }
    //       });
    //     }
    //   });

    //   console.log("Filtros preparados para guardar:", filtersToSave);
    //   console.log("Parámetros del servicio:", {
    //     accountId,
    //     tabId,
    //     centerFilterId,
    //     userId,
    //     filtersToSave,
    //   });

    //   // Solo llamar al servicio si hay filtros para guardar
    //   if (filtersToSave.length > 0) {
    //     // Llamar al servicio para guardar los filtros
    //     const saveResult = await saveControlFilters(
    //       accountId,
    //       tabId,
    //       centerFilterId,
    //       userId,
    //       filtersToSave
    //     );

    //     console.log("Filtros guardados exitosamente:", saveResult);
    //   } else {
    //     console.log("No hay filtros para guardar");
    //   }
    // } catch (error) {
    //   console.error("Error guardando filtros:", error);
    //   console.error("Detalles del error:", error.response?.data);
    // }

    setFilterValuesActions(filtersToApply);
  };

  // Función auxiliar para formatear valores según la configuración
  const formatValueAccordingToConfig = (value, format) => {
    if (format && value) {
      // Si value es un objeto Date de JavaScript, convertirlo al formato requerido
      if (value instanceof Date) {
        // Función para formatear dinámicamente basándose en el string de formato
        const formatDate = (date, formatString) => {
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, "0");
          const day = date.getDate().toString().padStart(2, "0");
          const hours = date.getHours().toString().padStart(2, "0");
          const minutes = date.getMinutes().toString().padStart(2, "0");
          const seconds = date.getSeconds().toString().padStart(2, "0");

          // Reemplazar tokens de formato con valores reales
          return formatString
            .replace(/YYYY/g, year)
            .replace(/YY/g, year.toString().slice(-2))
            .replace(/MM/g, month)
            .replace(/M/g, date.getMonth() + 1)
            .replace(/DD/g, day)
            .replace(/D/g, date.getDate())
            .replace(/HH/g, hours)
            .replace(/H/g, date.getHours())
            .replace(/mm/g, minutes)
            .replace(/m/g, date.getMinutes())
            .replace(/ss/g, seconds)
            .replace(/s/g, date.getSeconds());
        };

        return formatDate(value, format);
      }
      // Si value tiene un método format (como Moment.js o Day.js), usarlo
      if (typeof value.format === "function") {
        return value.format(format);
      }
    }
    // Si no hay formato o value es string, devolverlo tal como está
    return value;
  };

  // Función para aplicar filtros a cada panel
  const applyFiltersToEachPanel = async (filtersToApply) => {
    // Agrupar filtros por panelId para optimizar
    const filtersByPanel = filtersToApply.reduce((acc, filter) => {
      if (!acc[filter.panelId]) {
        acc[filter.panelId] = [];
      }
      acc[filter.panelId].push(filter);
      return acc;
    }, {});

    // Obtener todos los panelIds que se van a actualizar
    const panelIds = Object.keys(filtersByPanel);

    // Mostrar loading en todos los paneles que se van a actualizar
    console.log("🔄 Iniciando loading para paneles:", panelIds);
    panelIds.forEach((panelId) => {
      setPanelLoading(panelId, true);
    });

    try {
      // Aplicar filtros a cada panel
      for (const [panelId, panelFilters] of Object.entries(filtersByPanel)) {
        try {
          console.log(
            `🔄 Aplicando filtros al panel ${panelId}:`,
            panelFilters
          );

          // Transformar los filtros al formato esperado por el servicio
          const formattedFilters = panelFilters.map((filter) => ({
            name: filter.parameterName,
            value: filter.value,
          }));

          console.log(
            `📡 Filtros formateados para panel ${panelId}:`,
            formattedFilters
          );

          // Consumir el servicio con los filtros aplicados
          const panelDataWithFilters = await getConsumeDataByPanelIdWithFilters(
            parseInt(panelId),
            formattedFilters
          );
        let auxComparisonData = null;
        if (
          panelDataWithFilters.auxComparation &&
          panelDataWithFilters.auxComparation.data
        ) {
          auxComparisonData = panelDataWithFilters.auxComparation.data.map(
            (row) => {
              const obj = {};
              panelDataWithFilters.auxComparation.columns.forEach(
                (column, index) => {
                  obj[column] = row[index];
                }
              );
              return obj;
            }
          );
        }

        // Actualizar el panel en el dashboard usando GridStack Context
        // IMPORTANTE: Solo actualizar los data, NO la metadata (configuración del KPI)
        updatePanelData(parseInt(panelId), {
          data: panelDataWithFilters.data
            ? panelDataWithFilters.data.map((row) => {
                const obj = {};
                panelDataWithFilters.columns.forEach((column, index) => {
                  obj[column] = row[index];
                });
                return obj;
              })
            : [],
          //INCLUIR COMPARISO SI  ES KPI
          auxComparisonData,

          // NO incluir metadata para preservar la configuración original del KPI
        });

          console.log(
            `✅ Datos actualizados del panel ${panelId}:`,
            panelDataWithFilters
          );

          // Actualizar el panel en el dashboard usando GridStack Context
          // IMPORTANTE: Solo actualizar los data, NO la metadata (configuración del KPI)
          updatePanelData(parseInt(panelId), {
            data: panelDataWithFilters.data
              ? panelDataWithFilters.data.map((row) => {
                  const obj = {};
                  panelDataWithFilters.columns.forEach((column, index) => {
                    obj[column] = row[index];
                  });
                  return obj;
                })
              : [],
            isLoadingFilters: false, // Quitar loading cuando se complete
            // NO incluir metadata para preservar la configuración original del KPI
          });

          // Actualizar estado local de loading
          setPanelLoadingStates((prev) => ({
            ...prev,
            [panelId]: false,
          }));

          console.log(
            `✅ Panel ${panelId} actualizado exitosamente con filtros aplicados`
          );
        } catch (error) {
          console.error(
            `❌ Error aplicando filtros al panel ${panelId}:`,
            error
          );

          // Quitar loading en caso de error
          setPanelLoading(panelId, false);
        }
      }
    } catch (error) {
      console.error("❌ Error general aplicando filtros:", error);

      // Limpiar todos los loadings en caso de error general
      clearAllPanelLoadings();
    }
  };

  return (
    <div className={`panel-filter-sidebar${open ? " open" : ""}`}>
      <div className="panel-filter-header">
        <div className="panel-filter-title">
          <span className="icon-Filter_manager"></span>
          <label>Control Center</label>
        </div>
        <span className="spacer"></span>
        <button className="config-button" style={{ marginRight: "0.2rem" }}>
          <span className="icon-more-horizontal"></span>
        </button>
        <button onClick={onClose} className="config-button">
          <span className="icon-double-arrow"></span>
        </button>
      </div>

      <div className="btns-filters">
        <button onClick={handleApplyFilters}>Apply Filters</button>
        <button onClick={handleClearAllFilters}>Clear All Filters</button>
      </div>

      <div className="search-input-container">
        <input
          type="text"
          placeholder="Search Filters"
          className="search-input"
          value={searchValue}
          onChange={handleSearchChange}
        />
        <span className="icon-search search-icon"></span>
      </div>

      <div className="panel-filter-content">
        {loading && (
          <div className="loading-state">
            <span>Loading filters...</span>
          </div>
        )}

        {error && (
          <div className="error-state">
            <span>Error loading filters: {error}</span>
          </div>
        )}

        {!loading && !error && (
          <div className="filters-container">
            {/* Iterar por cada grupo de filtros filtrado */}
            {filteredFilters.length > 0 ? (
              filteredFilters.map((groupData, groupIndex) => {
                // Determinar si es un grupo (más de 1 filtro)
                const isGroup = groupData.filters.length > 1;

                return (
                  <div key={groupIndex} className="filter-item">
                    <div className="filter-item-drag-handle">
                      <span
                        className="icon-dragdrop"
                        style={{ color: "#e6e6e6" }}
                      ></span>
                    </div>

                    <div
                      style={{
                        width: "1px",
                        backgroundColor: "#f1f1f1",
                        height: "calc(100% - 1rem)",
                      }}
                    ></div>

                    <div className="filter-item-content">
                      {isGroup && (
                        <div className="filter-item-header">
                          <button className="config-button">
                            <span className="icon-more-horizontal"></span>
                          </button>
                          <span className="filter-item-title-group">
                            {groupData.filterGroup.label ||
                              groupData.filterGroup.filterGroupName}
                          </span>
                        </div>
                      )}

                      <div className="filters-content">
                        {/* Iterar por los filtros de este grupo */}
                        {groupData.filters.map((filterItem, filterIndex) => {
                          const filterType = filterItem.filter.filterType;

                          if (filterType === "datetime range") {
                            return (
                              <FilterDateTime
                                key={filterIndex}
                                filterData={filterItem}
                                groupData={groupData.filterGroup}
                                isGroup={isGroup}
                                returnValues={returnValues}
                                clearAllTrigger={clearAllTrigger}
                              />
                            );
                          } else if (filterType === "dropdown") {
                            return (
                              <FilterDropdown
                                key={filterIndex}
                                filterData={filterItem}
                                groupData={groupData.filterGroup}
                                isGroup={isGroup}
                                returnValues={returnValues}
                                clearAllTrigger={clearAllTrigger}
                                onApplyFilters={handleApplyFilters}
                              />
                            );
                          } else {
                            // Tipo de filtro no reconocido, mostrar información de debug
                            return (
                              <div key={filterIndex} className="unknown-filter">
                                <p>
                                  {/* Tipo de filtro no reconocido: "{filterType}" */}
                                </p>
                                <p>
                                  ID: {filterItem.id}, Label: {filterItem.label}
                                </p>
                                <p>
                                  Filter Name: {filterItem.filter.filterName}
                                </p>
                              </div>
                            );
                          }
                        })}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-filters-found">
                <span>No filters found matching {searchValue}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelFilter;
